import type { CSSProperties, ReactElement } from 'react';
import { ChevronLeft, List, Minus, Plus } from 'lucide-react';
import { Button, SegmentedControl, cn } from '@xdocs/ui';
import type { ReaderLayoutMode, ReaderThemeId, UseReaderPrefsResult } from '../../features/reader/index.js';
import { READER_LAYOUT_MODES, READER_THEME_IDS, READER_THEME_PALETTE } from '../../features/reader/index.js';
import { useReaderT } from '../../features/reader/useReaderT.js';
import styles from './ReaderToolbar.module.css';

export interface ReaderToolbarProps {
  progressLabel: string;
  prefs: UseReaderPrefsResult;
  onClose: () => void;
  onOpenChapters: () => void;
  className?: string | undefined;
}

/**
 * F66 reader header: close (back to the document), progress indicator,
 * theme swatches, font-size stepper, scroll/paginated toggle, chapter-jump
 * trigger. Reader-scoped controls only — never the app light/dark theme or
 * the F65 document Theme (CODE_GUIDELINES §4/§28.3).
 */
export function ReaderToolbar({ progressLabel, prefs, onClose, onOpenChapters, className }: ReaderToolbarProps): ReactElement {
  const { t } = useReaderT();

  const layoutOptions = READER_LAYOUT_MODES.map((mode: ReaderLayoutMode) => ({
    value: mode,
    label: t(`reader:layout.${mode}`),
  }));

  return (
    <header className={cn(styles['toolbar'], className)}>
      <Button variant="ghost" size="sm" leadingIcon={<ChevronLeft size={16} aria-hidden="true" />} onClick={onClose}>
        {t('reader:close')}
      </Button>

      <span className={cn(styles['progress'])} aria-live="polite">
        {progressLabel}
      </span>

      <div className={cn(styles['controls'])}>
        <div className={cn(styles['themeSwatches'])} role="radiogroup" aria-label={t('reader:theme.label')}>
          {READER_THEME_IDS.map((themeId: ReaderThemeId) => {
            const palette = READER_THEME_PALETTE[themeId];
            const active = prefs.themeId === themeId;
            return (
              <button
                key={themeId}
                type="button"
                role="radio"
                aria-checked={active}
                title={t(`reader:theme.${themeId}`)}
                className={cn(styles['swatch'], active && styles['swatchActive'])}
                style={{ '--reader-swatch-color': palette.background } as CSSProperties}
                onClick={() => prefs.setThemeId(themeId)}
              >
                <span className={cn(styles['visuallyHidden'])}>{t(`reader:theme.${themeId}`)}</span>
              </button>
            );
          })}
        </div>

        <div className={cn(styles['fontStepper'])}>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('reader:fontSize.decrease')}
            disabled={!prefs.canDecreaseFontSize}
            onClick={prefs.decreaseFontSize}
          >
            <Minus size={16} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('reader:fontSize.increase')}
            disabled={!prefs.canIncreaseFontSize}
            onClick={prefs.increaseFontSize}
          >
            <Plus size={16} aria-hidden="true" />
          </Button>
        </div>

        <SegmentedControl
          label={t('reader:layout.label')}
          options={layoutOptions}
          value={prefs.layoutMode}
          onChange={prefs.setLayoutMode}
          className={cn(styles['layoutToggle'])}
        />

        <Button variant="ghost" size="icon" aria-label={t('reader:chapters.title')} onClick={onOpenChapters}>
          <List size={18} aria-hidden="true" />
        </Button>
      </div>
    </header>
  );
}
